// API base, auth headers and fetch helpers shared by the admin tabs.

import { endAdminSession } from './adminAuth';

const runtimeHost = typeof window !== 'undefined'
    ? String(window.location.hostname || '').toLowerCase()
    : '';
const runtimeAdminOrigin = typeof window !== 'undefined' && runtimeHost.startsWith('admin.')
    ? window.location.origin
    : '';
export const API_BASE = String(
    process.env.REACT_APP_ADMIN_API_BASE_URL
    || process.env.REACT_APP_API_BASE_URL
    || runtimeAdminOrigin
    || 'https://api.xn--omrdeskollen-vcb.nu',
).replace(/\/+$/, '');

const AUTH_COOKIE_KEYS = ['admin_job_token', 'admin_token', 'access_token', 'token'];
const AUTH_STORAGE_KEYS = ['admin_job_token', 'admin_token', 'access_token', 'token'];

const stripBearerPrefix = (rawToken) => String(rawToken || '').trim().replace(/^bearer\s+/i, '').trim();

const readCookieToken = () => {
    if (typeof document === 'undefined') return '';
    const parts = String(document.cookie || '').split(';').map((part) => part.trim()).filter(Boolean);
    for (const key of AUTH_COOKIE_KEYS) {
        const match = parts.find((part) => part.startsWith(`${key}=`));
        if (!match) continue;
        const value = decodeURIComponent(match.slice(key.length + 1) || '').trim();
        if (value) return value;
    }
    return '';
};

const readStorageToken = () => {
    if (typeof window === 'undefined') return '';
    for (const key of AUTH_STORAGE_KEYS) {
        try {
            const localValue = window.localStorage?.getItem(key);
            if (localValue && String(localValue).trim()) return String(localValue).trim();
            const sessionValue = window.sessionStorage?.getItem(key);
            if (sessionValue && String(sessionValue).trim()) return String(sessionValue).trim();
        } catch (error) {
            return '';
        }
    }
    return '';
};

const resolveAuthToken = () => {
    const envToken = process.env.REACT_APP_ADMIN_JOB_BEARER_TOKEN
        || process.env.REACT_APP_ADMIN_BEARER_TOKEN
        || process.env.REACT_APP_JOB_BEARER_TOKEN
        || process.env.REACT_APP_BEARER_TOKEN;
    if (String(envToken || '').trim()) return String(envToken).trim();
    if (typeof window !== 'undefined' && String(window.__ADMIN_BEARER_TOKEN__ || '').trim()) {
        return String(window.__ADMIN_BEARER_TOKEN__).trim();
    }
    return readStorageToken() || readCookieToken();
};

export const buildAdminAuthHeaders = () => {
    const adminToken = stripBearerPrefix(resolveAuthToken());
    if (!adminToken) return {};
    // Authorization only: the proxy in front of the API (nginx on the Pi) answers the browser's preflight and allows
    // just Authorization and Content-Type, so another header (X-Admin-Token) made every admin call fail to fetch.
    return {
        Authorization: `Bearer ${adminToken}`,
    };
};

const shouldIncludeCredentials = (url) => {
    if (typeof window === 'undefined') return false;
    try {
        return new URL(url, window.location.origin).origin === window.location.origin;
    } catch (error) {
        return false;
    }
};

const adminUrl = (path) => (/^https?:\/\//.test(path) ? path : `${API_BASE}${path}`);

const adminFetch = async (path, options = {}) => {
    const url = adminUrl(path);
    const response = await fetch(url, {
        credentials: shouldIncludeCredentials(url) ? 'include' : 'omit',
        ...options,
        headers: {
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            ...buildAdminAuthHeaders(),
            ...(options.headers || {}),
        },
    });
    if (!response.ok) {
        let message = response.statusText || 'Request failed';
        try {
            const payload = JSON.parse(await response.text());
            message = payload?.error || payload?.message || message;
        } catch (error) {
            // Not JSON: keep the status text.
        }
        if (response.status === 404 && message === (response.statusText || 'Request failed')) {
            message = `the backend has no ${new URL(response.url).pathname} endpoint (is the latest backend deployed?)`;
        }
        const suffix = response.status === 401 ? ' (missing or invalid admin token)' : '';
        if (response.status === 401) endAdminSession();
        throw new Error(`${response.status}: ${message}${suffix}`);
    }
    return response;
};

export const fetchJson = async (path, options = {}) => {
    const response = await adminFetch(path, { ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } });
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch (error) {
        return { raw: text };
    }
};

export const postJson = (path, body) => fetchJson(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

// Downloads go through fetch so the admin token header is sent, then save the blob.
export const downloadFile = async (path, filename) => {
    const response = await adminFetch(path);
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
};
